// becky&lolo — Build-a-set, one step at a time.
// Each chosen product is added to the cart as its own line. Personalised lines get the
// same properties the product pages use (Personalisation, Font, Personalisation colour, Name)
// plus a hidden _Set reference so packing can see which lines belong together.
// The set discount is a discount code that only this builder applies, so the same items
// bought from normal product pages stay at full price.

try {
  await import(new URL('bl-personalisation-font-loader.js', import.meta.url).href);
} catch (error) {
  console.error('Personalisation fonts could not be loaded for the set builder.', error);
}

const FONTS = {
  'Alice Bold': ['"BL Alice", serif', '700'],
  'Bree Serif Bold': ['"BL Bree Serif", serif', '700'],
  'Arial Bold': ['Arial, sans-serif', '700'],
  'Salsa Bold': ['"BL Salsa", cursive', '700'],
  'Milonga Bold': ['"BL Milonga", cursive', '700'],
  'Monotype Corsiva Bold': ['"BL Monotype Corsiva", cursive', '700'],
};

// Theme families used to float matching lunch bags and bottles to the top.
const GROUPS = {
  dinosaur: ['jurassic', 'dinosaur', 'dinosaurs', 'dino', 'dinos'],
  unicorn: ['unicorn', 'unicorns'],
  transport: ['transport', 'transportation', 'vehicles', 'construction', 'digger', 'fire', 'engine', 'plane', 'aeroplane', 'cars'],
  jungle: ['jungle', 'safari', 'zoo', 'animals', 'koala', 'lion'],
  ballet: ['ballerina', 'ballet', 'bunny'],
  strawberry: ['strawberry', 'strawberries'],
  sea: ['mermaid', 'shark', 'sharks', 'fish'],
  space: ['space', 'rocket'],
  rainbow: ['rainbow'],
  floral: ['floral', 'flowers', 'flower', 'petals', 'bloom', 'butterfly'],
};
const STOP = new Set(['kids', 'toddler', 'backpack', 'rucksack', 'lunch', 'bag', 'with', 'side', 'pocket', 'water', 'bottle', 'small', '500ml', 'stainless', 'steel', 'insulated', 'large', 'quilted', 'girls', 'children', 's', 'the', 'and', 'a', 'magical', 'darling', 'classic', 'personalisable', 'front', 'double', 'decker']);

const words = (title) => (title || '').toLowerCase().replace(/[^a-z0-9 ]+/g, ' ').split(/\s+/).filter(Boolean);
const groupsOf = (ws) => new Set(Object.entries(GROUPS).filter(([, list]) => ws.some((w) => list.includes(w))).map(([g]) => g));

const matchScore = (a, b) => {
  if (!a || !b) return 0;
  const wa = words(a).filter((w) => !STOP.has(w));
  const wb = new Set(words(b).filter((w) => !STOP.has(w)));
  const shared = wa.filter((w) => wb.has(w)).length;
  const ga = groupsOf(wa);
  const gb = groupsOf([...wb]);
  const sharedGroups = [...ga].filter((g) => gb.has(g)).length;
  return shared * 2 + sharedGroups;
};

const KINDS = ['backpack', 'lunch', 'bottle'];
const NOUNS = { backpack: 'backpacks', lunch: 'lunch bags', bottle: 'bottles' };
const reducedMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;

class BlSetBuilder extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = 'true';

    this.nameMax = Number(this.dataset.nameMax) || 10;
    this.initialCount = Number(this.dataset.initialCount) || 10;
    // Set discount: percentage and code for backpack + lunch bag, and for the full set with a bottle.
    this.setPercent = Number(this.dataset.setPercent) || 0;
    this.setPercentFull = Number(this.dataset.setPercentFull) || 0;
    this.setCode = (this.dataset.setCode || '').trim();
    this.setCodeFull = (this.dataset.setCodeFull || '').trim();
    this.expanded = { backpack: false, lunch: false, bottle: false };
    this.step = 0;
    this.lastPointer = 0;
    try {
      this.moneyFormat = JSON.parse(this.querySelector('[data-money-format]')?.textContent || '"£{{amount}}"');
    } catch {
      this.moneyFormat = '£{{amount}}';
    }

    this.steps = [...this.querySelectorAll('[data-step]')];
    this.slots = [...this.querySelectorAll('[data-go]')];
    this.strip = this.querySelector('.blset-strip');
    this.nameInput = this.querySelector('[data-name]');
    this.fontSelect = this.querySelector('[data-font]');
    this.preview = this.querySelector('[data-preview]');
    this.liveInput = this.querySelector('[data-name-live]');
    this.measure = this.querySelector('[data-measure]');
    this.status = this.querySelector('[data-status]');
    this.nextBtn = this.querySelector('[data-next]');
    this.backBtn = this.querySelector('[data-back]');

    KINDS.forEach((kind) => {
      this.grid(kind)?.querySelectorAll('[data-card]').forEach((card, i) => { card.dataset.order = String(i); });
      this.applyVisibility(kind);
    });

    this.addEventListener('pointerdown', (event) => {
      if (event.target.closest?.('[data-card]')) this.lastPointer = Date.now();
    });
    this.addEventListener('change', (event) => this.onChange(event));
    // Tapping a card (even one already picked, like "No bottle") moves on to the next step.
    this.addEventListener('click', (event) => {
      const card = event.target.closest?.('[data-card]');
      if (!card || Date.now() - this.lastPointer > 1000) return;
      const kind = card.querySelector('input')?.dataset.kind;
      const stepIndex = KINDS.indexOf(kind);
      if (stepIndex !== this.step) return;
      clearTimeout(this.advanceTimer);
      this.advanceTimer = setTimeout(() => {
        if (card.querySelector('input')?.checked) this.go(stepIndex + 1);
      }, reducedMotion() ? 0 : 320);
    });
    this.addEventListener('input', (event) => {
      if (event.target === this.nameInput || event.target === this.liveInput) {
        // Both boxes edit the same name.
        const other = event.target === this.nameInput ? this.liveInput : this.nameInput;
        if (other.value !== event.target.value) other.value = event.target.value;
        this.clearError();
        this.render();
      }
    });
    this.querySelectorAll('[data-more]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const kind = btn.dataset.more;
        this.expanded[kind] = !this.expanded[kind];
        this.applyVisibility(kind);
      });
    });
    this.slots.forEach((slot) => slot.addEventListener('click', () => this.go(Number(slot.dataset.go))));
    this.backBtn.addEventListener('click', () => this.go(this.step - 1));
    this.nextBtn.addEventListener('click', () => {
      if (this.step < 3) this.go(this.step + 1);
      else this.addToCart();
    });
    document.fonts?.ready?.then(() => this.render());

    this.render();
  }

  grid(kind) {
    return this.querySelector(`[data-grid="${kind}"]`);
  }

  selected(kind) {
    const input = this.querySelector(`input[data-kind="${kind}"]:checked`);
    if (!input || input.hasAttribute('data-none')) return null;
    return {
      variantId: Number(input.value),
      productId: input.dataset.productId,
      title: input.dataset.title,
      price: Number(input.dataset.price) || 0,
      image: input.dataset.image,
      max: Number(input.dataset.max) || this.nameMax,
    };
  }

  // The discount this set will get: which code, what percentage, and roughly how much it saves
  // (Shopify works it out per item, so this matches to the penny in almost every case).
  currentOffer() {
    const backpack = this.selected('backpack');
    const lunch = this.selected('lunch');
    if (!backpack || !lunch) return null;
    const bottle = this.selected('bottle');
    const full = Boolean(bottle && this.setPercentFull && this.setCodeFull);
    const percent = full ? this.setPercentFull : this.setPercent;
    const code = full ? this.setCodeFull : this.setCode;
    if (!percent || !code) return null;
    const items = full ? [backpack, lunch, bottle] : [backpack, lunch];
    const saving = items.reduce((acc, item) => acc + Math.round((item.price * percent) / 100), 0);
    return { percent, code, saving, full };
  }

  // ---- Navigation ----------------------------------------------------------

  go(index, { focus = true } = {}) {
    const next = Math.max(0, Math.min(3, index));
    if (next === this.step) return;
    this.step = next;
    this.steps.forEach((el) => { el.hidden = Number(el.dataset.step) !== next; });
    this.render();

    // Bring the strip back into view if the customer has scrolled past it.
    const top = this.strip.getBoundingClientRect().top;
    if (top < 0) this.strip.scrollIntoView({ block: 'start', behavior: reducedMotion() ? 'auto' : 'smooth' });
    if (focus) this.steps[next].querySelector('.blset-step__title')?.focus({ preventScroll: true });
  }

  clearError() {
    if (this.status.dataset.state === 'error') {
      this.status.dataset.state = '';
      this.status.textContent = '';
    }
  }

  onChange(event) {
    this.clearError();
    const kind = event.target?.dataset?.kind;
    if (!kind) {
      this.render();
      return;
    }
    if (kind === 'backpack') {
      this.reorder('lunch');
      this.reorder('bottle');
    }
    if (kind === 'backpack' || kind === 'lunch') this.showColours(kind);
    this.applyVisibility(kind);
    this.render();
  }

  // ---- Products ------------------------------------------------------------

  reorder(kind) {
    const grid = this.grid(kind);
    const backpack = this.selected('backpack');
    if (!grid) return;
    const scored = [...grid.querySelectorAll('[data-card]')].map((card) => {
      const fixed = card.hasAttribute('data-fixed');
      const title = card.querySelector('input')?.dataset.title;
      const score = fixed || !backpack ? 0 : matchScore(backpack.title, title);
      return { card, fixed, score, order: Number(card.dataset.order) };
    });
    scored.sort((a, b) => {
      if (a.fixed !== b.fixed) return a.fixed ? -1 : 1;
      if (b.score !== a.score) return b.score - a.score;
      return a.order - b.order;
    });
    const best = Math.max(0, ...scored.map((s) => s.score));
    scored.forEach(({ card, score }) => {
      const badge = card.querySelector('[data-badge]');
      if (badge) badge.hidden = !(best > 0 && score >= Math.max(2, best - 1));
      grid.append(card);
    });
    const hint = this.querySelector(`[data-match-hint="${kind}"]`);
    if (hint) {
      const save = this.setPercent && this.setCode ? ` Get ${this.setPercent}% off when you buy them together.` : '';
      hint.textContent = best > 0
        ? `Lunch bags that match your backpack are shown first.${save}`
        : `Pick any lunch bag. It will have the same name as the backpack.${save}`;
    }
    this.applyVisibility(kind);
  }

  applyVisibility(kind) {
    const grid = this.grid(kind);
    const more = this.querySelector(`[data-more="${kind}"]`);
    if (!grid) return;
    const cards = [...grid.querySelectorAll('[data-card]')];
    const extra = kind === 'bottle' ? 1 : 0;
    const limit = this.initialCount + extra;
    cards.forEach((card, i) => {
      const checked = card.querySelector('input')?.checked;
      card.hidden = !this.expanded[kind] && i >= limit && !checked;
    });
    if (more) {
      const total = cards.length - extra;
      more.hidden = cards.length <= limit;
      more.textContent = this.expanded[kind] ? `Show fewer ${NOUNS[kind]}` : `Show all ${total} ${NOUNS[kind]}`;
      more.setAttribute('aria-expanded', String(this.expanded[kind]));
    }
  }

  showColours(kind) {
    const slot = this.querySelector(`[data-colour-slot="${kind}"]`);
    const item = this.selected(kind);
    if (!slot) return;
    let visible = false;
    slot.querySelectorAll('[data-colour-for]').forEach((box) => {
      const show = Boolean(item) && box.dataset.colourFor === item.productId && Boolean(box.querySelector('[data-bl-colour]'));
      box.hidden = !show;
      if (show) visible = true;
    });
    slot.hidden = !visible;
  }

  colourFor(kind) {
    const item = this.selected(kind);
    if (!item) return { required: false, value: '' };
    const box = this.querySelector(`[data-colour-slot="${kind}"] [data-colour-for="${item.productId}"]`);
    const options = box ? box.querySelectorAll('[data-bl-colour]') : [];
    const checked = box?.querySelector('[data-bl-colour]:checked');
    return { required: options.length > 0, value: checked?.value || '' };
  }

  nameLimit() {
    const limits = [this.nameMax];
    const bp = this.selected('backpack');
    const lb = this.selected('lunch');
    if (bp) limits.push(bp.max);
    if (lb) limits.push(lb.max);
    return Math.min(...limits);
  }

  money(cents) {
    const amount = (cents / 100).toFixed(2);
    return this.moneyFormat.replace(/\{\{\s*amount\w*\s*\}\}/, amount);
  }

  problems() {
    const issues = [];
    const name = this.nameInput.value.trim();
    if (!this.selected('backpack')) issues.push('choose a backpack');
    if (!this.selected('lunch')) issues.push('choose a lunch bag');
    if (!name) issues.push('add a name');
    else if (name.length > this.nameLimit()) issues.push(`shorten the name to ${this.nameLimit()} characters`);
    if (!this.fontSelect.value) issues.push('choose a font');
    if (this.colourFor('backpack').required && !this.colourFor('backpack').value) issues.push('pick a backpack name colour');
    if (this.colourFor('lunch').required && !this.colourFor('lunch').value) issues.push('pick a lunch bag name colour');
    return issues;
  }

  // ---- Rendering -----------------------------------------------------------

  setThumb(el, src) {
    el.replaceChildren();
    if (!src) return;
    const img = new Image();
    img.src = src;
    img.alt = '';
    img.decoding = 'async';
    el.append(img);
  }

  render() {
    const name = this.nameInput.value.trim();
    const items = KINDS.map((kind) => [kind, this.selected(kind)]);

    // Set strip.
    this.slots.forEach((slot) => {
      const i = Number(slot.dataset.go);
      if (i === this.step) slot.setAttribute('aria-current', 'step');
      else slot.removeAttribute('aria-current');
    });
    items.forEach(([kind, item]) => {
      const value = this.querySelector(`[data-slot-value="${kind}"]`);
      const thumb = this.querySelector(`[data-slot-thumb="${kind}"]`);
      const empty = { backpack: 'Choose', lunch: 'Choose', bottle: 'Optional' }[kind];
      value.textContent = item ? item.title : (kind === 'bottle' && this.step > 2 ? 'None' : empty);
      this.setThumb(thumb, item?.image);
      value.closest('.blset-slot').dataset.filled = String(Boolean(item));
    });
    const nameValue = this.querySelector('[data-slot-value="name"]');
    nameValue.textContent = name || 'Add';
    nameValue.closest('.blset-slot').dataset.filled = String(Boolean(name && this.fontSelect.value));

    // Name step: limit, preview (chosen font only, neutral colour), review.
    const limit = this.nameLimit();
    this.nameInput.maxLength = limit;
    this.liveInput.maxLength = limit;
    const help = this.querySelector('[data-name-help]');
    if (help) help.textContent = `Up to ${limit} characters, including spaces`;

    const font = FONTS[this.fontSelect.value];
    [this.liveInput, this.measure].forEach((el) => {
      el.style.fontFamily = font ? font[0] : '';
      el.style.fontWeight = font ? font[1] : '';
    });
    this.preview.dataset.empty = String(!name);
    if (this.step === 3) this.fitPreview();

    const offer = this.currentOffer();
    const saving = offer ? offer.saving : 0;
    const review = this.querySelector('[data-review]');
    review.replaceChildren(...items.filter(([, item]) => item).map(([kind, item]) => {
      const li = document.createElement('li');
      li.className = 'blset-review__row';
      const t = document.createElement('span');
      t.className = 'blset-review__name';
      t.textContent = item.title;
      const p = document.createElement('span');
      p.className = 'blset-review__price';
      p.textContent = this.money(item.price);
      const edit = document.createElement('button');
      edit.type = 'button';
      edit.className = 'blset-review__edit';
      edit.textContent = 'Change';
      edit.setAttribute('aria-label', `Change ${item.title}`);
      edit.addEventListener('click', () => this.go(KINDS.indexOf(kind)));
      li.append(t, p, edit);
      return li;
    }));
    if (offer && saving) {
      const li = document.createElement('li');
      li.className = 'blset-review__row blset-review__row--saving';
      const t = document.createElement('span');
      t.className = 'blset-review__name';
      t.textContent = `${offer.full ? 'Full set' : 'Set'} discount (${offer.percent}% off)`;
      const p = document.createElement('span');
      p.className = 'blset-review__price';
      p.textContent = `−${this.money(saving)}`;
      li.append(t, p, document.createElement('span'));
      review.append(li);
    }

    // Bar: total after the set discount.
    const chosen = items.filter(([, item]) => item);
    const sum = chosen.reduce((acc, [, item]) => acc + item.price, 0);
    const total = sum - saving;
    this.querySelector('[data-total]').textContent = this.money(total);
    const was = this.querySelector('[data-was]');
    was.hidden = !saving;
    was.textContent = saving ? this.money(sum) : '';
    const countParts = [chosen.length ? `${chosen.length} ${chosen.length === 1 ? 'item' : 'items'}` : 'No items yet'];
    if (offer && saving) countParts.push(`${offer.percent}% off`);
    this.querySelector('[data-count]').textContent = countParts.join(' · ');

    const bottleHint = this.querySelector('[data-bottle-hint]');
    if (bottleHint) {
      let lead = '';
      if (this.setPercentFull && this.setCodeFull) {
        lead = offer && offer.full
          ? `Your set gets ${offer.percent}% off. `
          : `Add a bottle and get ${this.setPercentFull}% off your whole set. `;
      }
      bottleHint.textContent = `${lead}Bottles aren't personalised.`;
    }

    this.backBtn.hidden = this.step === 0;
    const issues = this.problems();
    if (this.step === 0) {
      this.nextBtn.textContent = 'Next: Lunch Bag';
      this.nextBtn.disabled = !this.selected('backpack');
    } else if (this.step === 1) {
      this.nextBtn.textContent = 'Next: Bottle';
      this.nextBtn.disabled = !this.selected('lunch');
    } else if (this.step === 2) {
      this.nextBtn.textContent = this.selected('bottle') ? 'Next: Add Name' : 'Skip, Add Name';
      this.nextBtn.disabled = false;
    } else {
      this.nextBtn.innerHTML = 'Add <span class="blset-hide-sm">Set </span>to Basket';
      this.nextBtn.disabled = issues.length > 0 || this.busy;
    }

    if (!this.busy && this.status.dataset.state !== 'error') {
      if (this.step === 3 && issues.length) {
        this.status.dataset.state = 'pending';
        this.status.textContent = `To finish: ${issues.join(', ')}.`;
      } else {
        this.status.dataset.state = '';
        this.status.textContent = '';
      }
    }
  }

  // Shrink the typed name so it always fits the preview box.
  fitPreview() {
    const input = this.liveInput;
    const measure = this.measure;
    const max = matchMedia('(max-width: 640px)').matches ? 36 : 46;
    requestAnimationFrame(() => {
      const width = Math.max(input.clientWidth - 8, 1);
      let size = max;
      measure.textContent = input.value || input.placeholder;
      measure.style.fontSize = `${size}px`;
      let guard = 0;
      while (measure.scrollWidth > width && size > 16 && guard < 60) {
        size -= 1;
        measure.style.fontSize = `${size}px`;
        guard += 1;
      }
      input.style.fontSize = `${size}px`;
    });
  }

  // ---- Cart ----------------------------------------------------------------

  async addToCart() {
    if (this.busy) return;
    const issues = this.problems();
    if (issues.length) {
      this.render();
      return;
    }
    this.busy = true;
    this.nextBtn.disabled = true;
    this.nextBtn.dataset.loading = 'true';
    this.nextBtn.textContent = 'Adding…';
    this.status.dataset.state = 'pending';
    this.status.textContent = '';

    const setRef = `S-${Date.now().toString(36).slice(-5).toUpperCase()}`;
    const name = this.nameInput.value.trim();
    const font = this.fontSelect.value;
    const offer = this.currentOffer();

    const personalised = (kind) => {
      const item = this.selected(kind);
      const colour = this.colourFor(kind);
      const properties = { Personalisation: 'Yes please', Font: font };
      if (colour.value) properties['Personalisation colour'] = colour.value;
      properties.Name = name;
      properties._Set = setRef;
      return { id: item.variantId, quantity: 1, properties };
    };

    const lines = [personalised('backpack'), personalised('lunch')];
    const bottle = this.selected('bottle');
    if (bottle) lines.push({ id: bottle.variantId, quantity: 1, properties: { _Set: setRef } });

    // Ask for the cart sections the theme re-renders, exactly as the product form does.
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);
    const sectionsParam = sectionIds.length ? { sections: sectionIds.join(',') } : {};
    const root = window.Shopify?.routes?.root || '/';
    const addUrl = window.Theme?.routes?.cart_add_url || this.dataset.cartAddUrl;
    const cartJsUrl = `${root}cart.js`;
    const updateUrl = `${root}cart/update.js`;

    try {
      const response = await fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: lines, ...sectionsParam }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || 'Your set could not be added.');
      }

      // Apply the builder-only set discount code. If this fails the set is still in the
      // basket, just without the discount, so never block the add on it.
      let cart = null;
      let sections = data.sections;
      if (offer) {
        try {
          const res = await fetch(updateUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ discount: offer.code, ...sectionsParam }),
          });
          const updated = await res.json().catch(() => ({}));
          if (res.ok && Array.isArray(updated.items)) {
            cart = updated;
            if (updated.sections) sections = updated.sections;
          }
          const applied = (updated.discount_codes || [])
            .some((d) => String(d.code).toUpperCase() === offer.code.toUpperCase() && d.applicable);
          if (!applied) console.warn('Set builder: set discount code was not applied', offer.code, updated);
        } catch (error) {
          console.warn('Set builder: could not apply the set discount code', error);
        }
      }

      // Read the basket back and make sure the set is really in it.
      if (!cart) cart = await fetch(cartJsUrl, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      const inCart = (cart.items || []).filter((item) => item.properties && item.properties._Set === setRef);
      if (inCart.length < lines.length) {
        console.error('Set builder: add succeeded but set not found in cart', { setRef, lines, cart });
        throw new Error('Your basket did not update.');
      }

      // Tell the theme: updates the header count and refreshes/opens the cart drawer.
      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cart,
          sourceId: 'bl-set-builder',
          data: { source: 'bl-set-builder', itemCount: cart.item_count, sections },
        },
      }));

      this.busy = false;
      delete this.nextBtn.dataset.loading;
      this.nextBtn.textContent = 'Added to Basket';
      this.status.dataset.state = 'ready';
      this.status.textContent = 'Your set is in the basket.';

      const drawer = document.querySelector('cart-drawer-component');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
        setTimeout(() => this.render(), 2500);
      } else {
        window.location.href = this.dataset.cartUrl;
      }
    } catch (error) {
      this.busy = false;
      delete this.nextBtn.dataset.loading;
      this.status.dataset.state = 'error';
      this.status.textContent = `${error.message} Check your basket and choices, then try again.`;
      this.render();
    }
  }
}

if (!customElements.get('bl-set-builder')) customElements.define('bl-set-builder', BlSetBuilder);
